import { AuiIf, ThreadPrimitive } from '@assistant-ui/react';
import ButtonBase from '@mui/material/ButtonBase';

import { AgentWorkbenchReadOnlyBar } from './agent-workbench-read-only-bar.js';
import { AgentWorkbenchThreadHistory } from './agent-workbench-thread-history.js';
import { WorkbenchComposer } from './workbench-composer.js';
import { WorkbenchRunErrorBanner } from './workbench-run-error-banner.js';
import { AssistantMessage } from './messages/assistant-message.js';
import { ModelSetDivider } from './messages/model-set-divider.js';
import { UserMessage } from './messages/user-message.js';
import { ThinkingIndicator } from './thinking-indicator.js';
import { useAgentRunTitleStatus } from './use-agent-run-title-status.js';
import { useNotifyOnRunComplete } from './use-notify-on-run-complete.js';

import type { ReactNode } from 'react';
import type { UserAvatarUser } from '#components/user-avatar/user-avatar.js';

/**
 * How much of the panel the viewer can act on:
 * - `interactive` — full chrome and a working composer (submit + browse).
 * - `read-only-show-chrome` — the conversation-history chrome is shown for
 *   browsing, but the composer is replaced with a read-only status bar (browse,
 *   no submit).
 * - `read-only-hide-chrome` — bare message viewport only; no history chrome and
 *   no composer (no submit, no browse).
 */
export type AgentWorkbenchInteractionMode = 'interactive' | 'read-only-show-chrome' | 'read-only-hide-chrome';

export interface AgentWorkbenchPanelProps {
	placeholder: string;
	/** Interaction level of the panel. @default 'interactive' */
	interactionMode?: AgentWorkbenchInteractionMode;
	/** Optional copy shown when the selected conversation contains no messages. */
	emptyStateMessage?: string;
	/** Conversation owner to show on user messages when they were not sent by the current user. */
	userMessageAuthor?: UserAvatarUser;
	/** Optional banner rendered above the message thread (e.g. a disclaimer). */
	headerBanner?: ReactNode;
	/** Optional hint rendered directly above the composer (e.g. a quiet AI disclaimer). */
	composerHint?: ReactNode;
	/** Called when a history row selects a conversation. */
	onSelectThread?: (conversationId: string) => void;
	/** Called when the user starts a new conversation from the history chrome. */
	onNewThread?: () => void;
	allowThreadCreation?: boolean;
	allowThreadDeletion?: boolean;
	/**
	 * Optional dock rendered between the thread viewport and the composer. It
	 * stays fixed while the thread scrolls (e.g. the ingest-narration task list).
	 */
	footerDock?: ReactNode;
	/**
	 * Optional element rendered in the conversation toolbar, before the
	 * notification toggle (e.g. a usage indicator). Dropped along with the rest
	 * of the chrome in the bare read-only mode.
	 */
	headerAccessory?: ReactNode;
}

/**
 * Reusable agent workbench panel: message viewport + composer. Must be rendered inside an
 * `AssistantRuntimeProvider`.
 *
 * @param props.placeholder - Composer input placeholder text.
 * @param props.headerBanner - Optional sticky banner above the thread (e.g. disclaimer).
 * @param props.composerHint - Optional hint directly above the composer (e.g. a quiet AI disclaimer).
 * @param props.interactionMode - Interaction level: interactive composer, read-only with
 *   browsing chrome, or a bare read-only viewport. @default 'interactive'
 * @param props.emptyStateMessage - Copy shown when the selected conversation has no messages.
 * @param props.userMessageAuthor - Owner shown on user messages not sent by the current user.
 * @param props.onSelectThread - Called when a history row selects a conversation.
 * @param props.onNewThread - Called when the user starts a new conversation.
 * @param props.allowThreadCreation - Whether the history toolbar can start a new conversation.
 * @param props.allowThreadDeletion - Whether history rows can be deleted.
 * @param props.footerDock - Optional fixed dock between the thread and the composer.
 * @param props.headerAccessory - Optional element in the conversation toolbar before the notification toggle.
 */
export function AgentWorkbenchPanel({
	placeholder,
	headerBanner,
	composerHint,
	interactionMode = 'interactive',
	emptyStateMessage,
	userMessageAuthor,
	onSelectThread,
	onNewThread,
	allowThreadCreation = true,
	allowThreadDeletion = true,
	footerDock,
	headerAccessory,
}: AgentWorkbenchPanelProps) {
	// Chrome around the viewport — history above, run-failure notice below — shows in
	// every mode except the bare read-only one.
	const showChrome = interactionMode !== 'read-only-hide-chrome';

	// The chrome carries the notification toggle, so it also decides whether this
	// surface may notify: without it a preference set elsewhere would make a bare
	// narration panel announce runs with no control to stop it, and with it a
	// visible bell would otherwise promise notifications it never delivers.
	useNotifyOnRunComplete({ enabled: showChrome });
	// Gated the same way, so one surface owns both out-of-band signals about a run.
	useAgentRunTitleStatus({ enabled: showChrome });

	return (
		<ThreadPrimitive.Root className='relative flex min-h-0 grow flex-col overflow-hidden'>
			{showChrome && (
				<AgentWorkbenchThreadHistory
					onSelectThread={onSelectThread}
					onNewThread={onNewThread}
					allowThreadCreation={allowThreadCreation}
					allowThreadDeletion={allowThreadDeletion}
					accessory={headerAccessory}
				/>
			)}
			<div className='relative isolate z-10 flex flex-col self-stretch'>{headerBanner}</div>
			<div className='relative flex grow flex-col overflow-hidden'>
				<ThreadPrimitive.Viewport className='min-h-0 grow basis-0 overflow-y-auto bg-gray-2 px-3 py-3.5'>
					{emptyStateMessage && (
						<AuiIf condition={(s) => s.thread.isEmpty}>
							<div className='flex min-h-full items-center justify-center px-6 text-center text-gray-11'>
								{emptyStateMessage}
							</div>
						</AuiIf>
					)}
					<ThreadPrimitive.Messages>
						{({ message }) => (
							<>
								<ModelSetDivider />
								{message.role === 'user' ?
									<UserMessage author={userMessageAuthor} />
								:	<AssistantMessage />}
							</>
						)}
					</ThreadPrimitive.Messages>
					<AuiIf condition={(s) => s.thread.isRunning}>
						<ThinkingIndicator />
					</AuiIf>
				</ThreadPrimitive.Viewport>
				<ThreadPrimitive.ScrollToBottom asChild>
					<ButtonBase className='absolute right-5 bottom-4 rounded-full border border-gray-a4 bg-gray-2 px-3 py-1.5 text-sm text-gray-a9 hover:bg-gray-a2 disabled:hidden'>
						↓
					</ButtonBase>
				</ThreadPrimitive.ScrollToBottom>
			</div>
			{footerDock}
			{/* Suppressed in the bare mode, where the banner's "send your message again"
			    remedy would have no composer to use. */}
			{showChrome && <WorkbenchRunErrorBanner />}
			{/* The hint shares the composer's block so it sits on the same
			    background, above the composer's own top border. */}
			<div className='shrink-0 bg-gray-2'>
				{composerHint}
				{interactionMode === 'interactive' ?
					<WorkbenchComposer placeholder={placeholder} />
				: interactionMode === 'read-only-show-chrome' ?
					<AgentWorkbenchReadOnlyBar />
				:	null}
			</div>
		</ThreadPrimitive.Root>
	);
}
